import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, X, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { format } from "date-fns";

/**
 * Google Sheets strips leading zeros from CSV exports, so "093673" arrives
 * here as "93673". Zero-pad any all-digit id back to the canonical 6-char
 * width so the roster matches what's already in the database.
 */
const STUDENT_ID_PAD = 6;
const normalizeStudentId = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  return /^\d+$/.test(s) ? s.padStart(STUDENT_ID_PAD, "0") : s;
};

export default function UploadGradeReport({ onClose, onSuccess }) {
  const [reportDate, setReportDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [progress, setProgress] = useState("");
  const [manualData, setManualData] = useState("");

  // Fetch exclusion list
  const { data: settings = [] } = useQuery({
    queryKey: ['settings'],
    queryFn: () => base44.entities.Settings.list(),
  });

  const exclusionSettings = settings.find(s => s.setting_key === 'excluded_students') || {
    excluded_student_ids: []
  };

  const excludedIds = Array.isArray(exclusionSettings.excluded_student_ids) 
    ? exclusionSettings.excluded_student_ids 
    : [];

  const processManualData = async () => {
    if (!manualData.trim()) {
      setError("Please paste your failure report data");
      return;
    }

    setProcessing(true);
    setError(null);
    setProgress("Parsing data...");

    try {
      const lines = manualData.trim().split('\n').map(line => line.trim()).filter(line => line);
      
      if (lines.length === 0) {
        throw new Error("No data found");
      }

      // Parse first line to find column indices
      const headerLine = lines[0];
      const headers = headerLine.split('\t').map(h => h.trim().toLowerCase());
      
      // Map all column indices
      const colMap = {
        campus: headers.findIndex(h => h.includes('campus')),
        studentLast: headers.findIndex(h => h.includes('student last name') || h === 'student last'),
        studentFirst: headers.findIndex(h => h.includes('student first name') || h === 'student first'),
        otherId: headers.findIndex(h => h.includes('other id')),
        studentId: headers.findIndex(h => h === 'student id'),
        methodOfInstruction: headers.findIndex(h => h.includes('method of instruction')),
        gradeLevel: headers.findIndex(h => h.includes('grade level')),
        course: headers.findIndex(h => h === 'course'),
        section: headers.findIndex(h => h === 'section'),
        courseShortDesc: headers.findIndex(h => h.includes('course short description')),
        teacher: headers.findIndex(h => h === 'teacher'),
        teacherEmail: headers.findIndex(h => h.includes('teacher email') || h === 'teacher e-mail'),
        assignment: headers.findIndex(h => h === 'assignment' && !h.includes('grade')),
        assignmentGrade: headers.findIndex(h => h.includes('assignment grade')),
        athleticGrade: headers.findIndex(h => h.includes('athletic grade')),
        comments: headers.findIndex(h => h === 'comments'),
        date: headers.findIndex(h => h === 'date'),
        time: headers.findIndex(h => h === 'time'),
        f1g1Phone: headers.findIndex(h => h.includes('f1g1 parent phone')),
        f1g2Phone: headers.findIndex(h => h.includes('f1g2 parent phone')),
        studentEmail: headers.findIndex(h => h.includes('student email')),
        f1g1Email: headers.findIndex(h => h.includes('f1g1 parent email')),
        f2g1Phone: headers.findIndex(h => h.includes('f2g1 parent phone')),
        f2g1Email: headers.findIndex(h => h.includes('f2g1 parent email')),
      };

      // Check required columns
      if (colMap.otherId === -1 || colMap.studentLast === -1 || colMap.studentFirst === -1 || colMap.teacher === -1) {
        throw new Error("Missing required columns (Other ID, Student Last Name, Student First Name, or Teacher)");
      }

      setProgress("Processing entries...");

      const existingEntries = await base44.entities.RosterEntry.list();
      // Load all students once so we can enrich each roster entry with the correct student_email
      // (the CSV usually only has other_id / student_id, not email)
      const allStudents = await base44.entities.Student.list();
      const studentByStudentId = {};
      for (const s of allStudents) {
        if (s.student_id) {
          // Index by the CANONICAL normalized id so leading-zero variants
          // ("66801" vs "066801") both resolve to the same student.
          const norm = normalizeStudentId(s.student_id);
          if (norm) studentByStudentId[norm] = s;
          // Legacy fallback: also index by the raw value for pre-normalization data
          studentByStudentId[String(s.student_id).trim()] = s;
        }
      }
      let skippedCount = 0;
      let missingEmailCount = 0;
      let excludedCount = 0;
      
      const duplicateIds = [];
      const entriesToCreate = [];

      // STEP 1: Parse all data and identify duplicates (NO API CALLS)
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const cols = line.split('\t').map(c => c.trim());

        setProgress(`Parsing ${i} of ${lines.length - 1}...`);

        // Normalize other_id to the canonical 6-char form as soon as it's
        // read from the CSV — Google Sheets strips leading zeros on export.
        const otherId = normalizeStudentId(cols[colMap.otherId]);

        // CHECK EXCLUSION LIST FIRST — also compared against the normalized id
        if (excludedIds.includes(otherId)) {
          excludedCount++;
          continue;
        }

        // Get athletic grade and check if it's below 70
        const athleticGrade = colMap.athleticGrade !== -1 ? cols[colMap.athleticGrade] : '';
        const athleticGradeNum = athleticGrade ? parseFloat(athleticGrade) : null;
        
        // Skip if athletic grade is 70 or above
        if (athleticGradeNum !== null && athleticGradeNum >= 70) {
          skippedCount++;
          continue;
        }

        const studentLast = cols[colMap.studentLast];
        const studentFirst = cols[colMap.studentFirst];
        const teacher = cols[colMap.teacher];
        const teacherEmail = colMap.teacherEmail !== -1 ? cols[colMap.teacherEmail] : '';

        // Track if teacher email is missing
        if (!teacherEmail) {
          missingEmailCount++;
        }

        // Skip if missing required fields
        if (!otherId || !studentLast || !studentFirst || !teacher) {
          skippedCount++;
          continue;
        }

        // Identify duplicates (same normalized other_id + teacher + course)
        const courseShortDesc = colMap.courseShortDesc !== -1 ? cols[colMap.courseShortDesc] : '';
        const duplicates = existingEntries.filter(e =>
          normalizeStudentId(e.other_id) === otherId &&
          e.teacher === teacher &&
          e.course_short_description === courseShortDesc &&
          e.status === 'active'
        );

        // Collect duplicate IDs to delete later
        duplicates.forEach(dup => {
          if (!duplicateIds.includes(dup.id)) {
            duplicateIds.push(dup.id);
          }
        });

        // Prepare entry to create later
        const csvStudentEmail = colMap.studentEmail !== -1 ? cols[colMap.studentEmail] : '';
        const csvStudentId = colMap.studentId !== -1 ? cols[colMap.studentId] : '';
        // Look up the student by normalized id
        const matchedStudent = studentByStudentId[otherId] || null;
        const enrichedEmail = csvStudentEmail || matchedStudent?.email || '';
        // student_id stored on the entry must also use the canonical form
        const enrichedStudentId = normalizeStudentId(csvStudentId) || matchedStudent?.student_id || otherId;

        entriesToCreate.push({
          attendance_status: 'pending',
          campus: colMap.campus !== -1 ? cols[colMap.campus] : '',
          student_last_name: studentLast,
          student_first_name: studentFirst,
          other_id: otherId,
          student_id: enrichedStudentId,
          method_of_instruction: colMap.methodOfInstruction !== -1 ? cols[colMap.methodOfInstruction] : '',
          grade_level: colMap.gradeLevel !== -1 ? cols[colMap.gradeLevel] : (matchedStudent?.grade_level || ''),
          course: colMap.course !== -1 ? cols[colMap.course] : '',
          section: colMap.section !== -1 ? cols[colMap.section] : '',
          course_short_description: courseShortDesc,
          teacher: teacher,
          teacher_email: teacherEmail,
          assignment: colMap.assignment !== -1 ? cols[colMap.assignment] : '',
          assignment_grade: colMap.assignmentGrade !== -1 ? cols[colMap.assignmentGrade] : '',
          athletic_grade: athleticGrade,
          comments: colMap.comments !== -1 ? cols[colMap.comments] : '',
          date: colMap.date !== -1 ? cols[colMap.date] : '',
          time: colMap.time !== -1 ? cols[colMap.time] : '',
          f1g1_parent_phone: colMap.f1g1Phone !== -1 ? cols[colMap.f1g1Phone] : '',
          f1g2_parent_phone: colMap.f1g2Phone !== -1 ? cols[colMap.f1g2Phone] : '',
          student_email: enrichedEmail,
          f1g1_parent_email: colMap.f1g1Email !== -1 ? cols[colMap.f1g1Email] : '',
          f2g1_parent_phone: colMap.f2g1Phone !== -1 ? cols[colMap.f2g1Phone] : '',
          f2g1_parent_email: colMap.f2g1Email !== -1 ? cols[colMap.f2g1Email] : '',
          report_date: reportDate,
          status: 'active'
        });
      }

      // STEP 2: Delete duplicates in parallel batches
      setProgress(`Deleting ${duplicateIds.length} duplicates...`);
      const deleteBatchSize = 100;
      for (let i = 0; i < duplicateIds.length; i += deleteBatchSize) {
        const batch = duplicateIds.slice(i, i + deleteBatchSize);
        await Promise.all(batch.map(id => base44.entities.RosterEntry.delete(id)));
      }

      const addedCount = entriesToCreate.length;
      const duplicatesRemoved = duplicateIds.length;

      // STEP 3a: Create the GradeReport FIRST so every roster entry can be
      // stamped with report_id. This lets a later delete-report operation
      // cascade-delete exactly the roster entries this report produced.
      const savedReport = await base44.entities.GradeReport.create({
        report_date: reportDate,
        file_url: "manual_entry",
        processed: true,
        entries_added: addedCount,
        duplicates_removed: duplicatesRemoved,
        upload_notes: `Added ${addedCount} entries, removed ${duplicatesRemoved} duplicates (same Other ID + Teacher + Course)${excludedCount > 0 ? `, excluded ${excludedCount} withdrawn students` : ''}, skipped ${skippedCount} (athletic grade >= 70 or incomplete)${missingEmailCount > 0 ? `. WARNING: ${missingEmailCount} entries missing teacher email - teachers won't see these on their roster` : ''}`
      });
      const reportId = savedReport?.id;
      // STEP 3b: Stamp every new entry with report_id, then bulk create.
      setProgress(`Creating ${entriesToCreate.length} new entries...`);
      const stampedEntries = entriesToCreate.map((e) => ({ ...e, report_id: reportId }));
      await base44.entities.RosterEntry.bulkCreate(stampedEntries);

      setSuccess(`✅ Success! Added ${addedCount} students, removed ${duplicatesRemoved} duplicates${excludedCount > 0 ? `, excluded ${excludedCount} withdrawn students` : ''}${skippedCount > 0 ? `, skipped ${skippedCount}` : ''}${missingEmailCount > 0 ? `. ⚠️ ${missingEmailCount} entries missing teacher email` : ''}.`);
      
      setTimeout(() => {
        onSuccess();
      }, 2500);

    } catch (err) {
      console.error("Processing error:", err);
      setError(err.message || "Failed to process data. Make sure you copied the complete data with all columns.");
    } finally {
      setProcessing(false);
      setProgress("");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <Card className="w-full max-w-4xl my-8">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Failure Report
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose} disabled={processing}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="bg-green-50 text-green-900 border-green-200">
              <CheckCircle className="w-4 h-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {progress && processing && (
            <Alert className="bg-blue-50 text-blue-900 border-blue-200">
              <Loader2 className="w-4 h-4 animate-spin" />
              <AlertDescription>{progress}</AlertDescription>
            </Alert>
          )}

          {excludedIds.length > 0 && (
            <Alert className="bg-purple-50 border-purple-200">
              <AlertDescription className="text-purple-900">
                <strong>🛡️ Auto-Exclusion Active:</strong> {excludedIds.length} student{excludedIds.length > 1 ? 's' : ''} will be automatically excluded if they appear on this report (withdrawn/not eligible).
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reportDate">Report Date</Label>
              <Input
                id="reportDate"
                type="date"
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
                disabled={processing}
              />
            </div>

            <div className="space-y-2">
              <Label>Paste Failure Report Data</Label>
              <Alert className="mb-4 bg-blue-50 border-blue-200">
                <AlertDescription className="text-sm space-y-2">
                  <div>
                    <strong>📋 Instructions:</strong>
                  </div>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    <li>Open your Excel/CSV failure report</li>
                    <li><strong>Select ALL columns</strong> from header row down</li>
                    <li><strong>⚠️ IMPORTANT: Include "Teacher Email" column</strong> (required for teacher access)</li>
                    <li>Copy (Ctrl+C or Cmd+C)</li>
                    <li>Paste below (Ctrl+V or Cmd+V)</li>
                    <li>Click &quot;Process Report&quot;</li>
                  </ol>
                  <div className="text-xs text-blue-700 mt-2">
                    ℹ️ System automatically removes duplicates (same Other ID + Teacher + Course). Only includes Athletic Grade &lt; 70.
                  </div>
                  <div className="text-xs text-orange-700 mt-2">
                    ⚠️ <strong>Teacher Email is critical:</strong> Without it, teachers won't be able to see their students in the system!
                  </div>
                  {excludedIds.length > 0 && (
                    <div className="text-xs text-purple-700 mt-2">
                      🛡️ <strong>Excluded Students:</strong> {excludedIds.length} student ID{excludedIds.length > 1 ? 's are' : ' is'} on the exclusion list and will be automatically skipped.
                    </div>
                  )}
                </AlertDescription>
              </Alert>
              <Textarea
                value={manualData}
                onChange={(e) => {
                  setManualData(e.target.value);
                  setError(null);
                }}
                placeholder="Paste complete failure report with ALL columns here (must include Teacher Email)..."
                className="h-64 font-mono text-xs"
                disabled={processing}
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={processing}>
              Cancel
            </Button>
            <Button 
              onClick={processManualData}
              disabled={!manualData.trim() || processing}
              className="bg-red-700 hover:bg-red-800"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Process Report
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}